import { safeJsonParse } from "@swiggy-private/js-utils";
import { ILiveNotificationBridgeData } from "../types";

/**
 * Centralised dispatcher for `window.globalJSBridgeHandler`.
 *
 * Native invokes `window.globalJSBridgeHandler(jsonString)` from inside the
 * webview. There is only one such global slot, but multiple features (Razorpay,
 * Live Notifications, ...) need to react to it. To keep them from clobbering
 * each other, each feature registers a handler keyed by `payload.type` and the
 * dispatcher routes the call to the matching handler.
 *
 * The handler's synchronous truthy return value is the ack to native — same
 * contract Razorpay's `ALLOW_THIRD_PARTY_INTENT` handler already relies on.
 *
 * Usage:
 *   // Once, at app init:
 *   installGlobalJSBridgeHandler();
 *
 *   // Per feature, inside a useEffect:
 *   useEffect(() => registerJSBridgeHandler("MY_TYPE", (data) => {
 *       // do work
 *       return true;
 *   }), []);
 */

interface IBridgePayload {
    type: string;
    data: ILiveNotificationBridgeData;
}

export type BridgeHandler = (data: ILiveNotificationBridgeData) => boolean | void;

const registry = new Map<string, BridgeHandler>();

export const registerJSBridgeHandler = (type: string, handler: BridgeHandler): (() => void) => {
    registry.set(type, handler);
    return () => {
        if (registry.get(type) === handler) {
            registry.delete(type);
        }
    };
};

const handleBridgeMessage = (raw: string): boolean | void => {
    const payload = safeJsonParse<Partial<IBridgePayload>>(raw, {});
    const type = payload?.type;
    if (!type) {
        return;
    }
    return registry.get(type)?.(payload.data ?? {});
};

let installed = false;

export const installGlobalJSBridgeHandler = (): void => {
    if (typeof window === "undefined" || installed) {
        return;
    }
    installed = true;
    window.globalJSBridgeHandler = handleBridgeMessage;
};
